/**
 * Watchlist, grouped by asset class as the spec requires.
 *
 * Clicking a row drives everything else on the page -- chart, signal panel,
 * news feed -- over the WebSocket, with no reload.
 *
 * Once the universe reached 143 symbols across four asset classes a flat
 * list stopped being usable. The filter is two-tier: an asset-class tab
 * first, then whatever second cut that class actually has -- industries for
 * equities, a turnover/change sort for crypto, major-versus-cross for FX,
 * nothing for gold, which is one symbol. Offering the same second tier to
 * every class is what produced "Tài chính" listing BTCUSDT.
 *
 * Search sits above the tabs and ignores them: typing "VCB" while looking
 * at Crypto finds it and moves the tab, rather than returning nothing and
 * leaving the reader to guess which tab to try. It also reaches symbols
 * that are NOT on a watchlist -- with 143 tickers seeded the common case is
 * "find HPG", not "scroll".
 */

import { useEffect, useMemo, useState } from 'react'

import type { AssetClass, Symbol, Watchlist as WatchlistType, WatchlistItem } from '@/api/types'
import {
  useAddToWatchlist, useMarketSignals, useMute, useSymbols, useWatchlists,
} from '@/hooks/useQueries'
import { useAppStore } from '@/store/useAppStore'
import { formatPrice } from '@/components/Chart/ChartPanel'
import {
  ASSET_LABELS as ASSET_TAB_LABELS,
  AssetTabs,
  FX_MAJORS,
  Highlight,
  assetCounts,
  matchesFilters,
  matchesQuery,
  normalize,
  signalsFirst,
  SignalDot,
  topSignalByTicker,
} from '@/components/filters/assetFilter'
import type { TickerSignal } from '@/components/filters/assetFilter'

const INDUSTRY_LABELS: Record<string, string> = {
  financials: 'Tài chính',
  industrials: 'Công nghiệp',
  real_estate: 'Bất động sản',
  consumer_staples: 'Tiêu dùng thiết yếu',
  consumer_discretionary: 'Tiêu dùng không thiết yếu',
  materials: 'Nguyên vật liệu',
  energy: 'Năng lượng',
  utilities: 'Tiện ích',
  technology: 'Công nghệ',
  health_care: 'Y tế',
  unclassified: 'Chưa phân ngành',
}

export function Watchlist({ livePrices }: { livePrices: Record<number, number> }) {
  const { data, isLoading, error } = useWatchlists()
  const { data: allSymbols } = useSymbols()
  // Same query the signal tab runs, so this shares one cache entry rather
  // than adding a request.
  const { data: marketSignals } = useMarketSignals()
  const selected = useAppStore((s) => s.selectedTicker)
  const selectTicker = useAppStore((s) => s.selectTicker)

  const assetTab = useAppStore((s) => s.assetTab)
  const setAssetTab = useAppStore((s) => s.setAssetTab)
  const query = useAppStore((s) => s.symbolQuery)
  const setQuery = useAppStore((s) => s.setSymbolQuery)
  const cryptoSort = useAppStore((s) => s.cryptoSort)
  const setCryptoSort = useAppStore((s) => s.setCryptoSort)

  const [industry, setIndustry] = useState<string | null>(null)

  const groups = useMemo(() => groupByAssetClass(data ?? []), [data])
  const needle = normalize(query.trim())

  // Counts come from the symbol universe, not from a constant: the crypto
  // list is rebuilt nightly from Binance turnover, so a hardcoded 30 would
  // be wrong the first time a pair drops out.
  const counts = useMemo(() => assetCounts(allSymbols ?? []), [allSymbols])

  // An industry is a Vietnamese-equity concept. The chips are built only
  // from equities and only rendered on that tab; leaving a stale industry
  // selected while the reader moves to Crypto is how "Tài chính" came to
  // list BTCUSDT, so the selection is cleared on any tab change.
  const industries = useMemo(() => {
    const counts = new Map<string, number>()
    for (const symbol of allSymbols ?? []) {
      if (symbol.asset_class !== 'stock_vn' || !symbol.industry_code) continue
      counts.set(symbol.industry_code, (counts.get(symbol.industry_code) ?? 0) + 1)
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1])
  }, [allSymbols])

  const chooseTab = (tab: typeof assetTab) => {
    setAssetTab(tab)
    if (tab !== 'stock_vn') setIndustry(null)
  }

  // Where the search actually found something. A reader who types "VCB"
  // while looking at Crypto is asking for VCB, not for an empty list -- so
  // the tab follows the answer instead of hiding it.
  const hitTabs = useMemo(() => {
    if (!needle) return []
    const found = new Set<AssetClass>()
    for (const symbol of allSymbols ?? []) {
      if (matchesQuery(symbol, needle)) found.add(symbol.asset_class)
    }
    return [...found]
  }, [allSymbols, needle])

  useEffect(() => {
    if (!needle || hitTabs.length === 0) return
    if (assetTab === 'all' || hitTabs.includes(assetTab as AssetClass)) return
    // Only when the current tab has nothing: switching while the reader can
    // already see a match would yank the list out from under them.
    chooseTab(hitTabs.length === 1 ? hitTabs[0] : 'all')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needle, hitTabs, assetTab])

  // Symbols matching the search that are not already on a watchlist. With a
  // hundred tickers seeded, most searches land here rather than in the list.
  const watchedIds = useMemo(
    () => new Set((data ?? []).flatMap((w) => w.items.map((i) => i.symbol.id))),
    [data],
  )
  const unwatchedMatches = useMemo(() => {
    if (needle.length < 1) return []
    return (allSymbols ?? [])
      .filter(
        (symbol) =>
          !watchedIds.has(symbol.id) &&
          matchesFilters(symbol, { assetTab, industry, needle }),
      )
      .slice(0, 12)
  }, [allSymbols, watchedIds, needle, industry, assetTab])

  const matches = (item: WatchlistItem) =>
    matchesFilters(item.symbol, { assetTab, industry, needle })

  // The second tier, per asset class. Crypto has no industries, so it gets
  // an ordering instead: turnover answers "what is being traded today",
  // change answers "what moved".
  const openSignals = useMemo(() => topSignalByTicker(marketSignals), [marketSignals])

  const orderFor = (assetClass: AssetClass, items: WatchlistItem[]) => {
    let ordered = items
    if (assetClass === 'crypto') {
      const key = (item: WatchlistItem) =>
        cryptoSort === 'turnover' ? (item.turnover_24h ?? 0) : Math.abs(item.change_pct_24h ?? 0)
      ordered = [...items].sort((a, b) => key(b) - key(a))
    }
    // Anything with an open idea goes to the top of its own asset class,
    // strongest first. A row the reader is meant to act on should not be
    // forty rows down because its 24h turnover happens to be small.
    return signalsFirst(ordered, (item) => item.symbol.ticker, openSignals)
  }

  const visibleCount =
    groups.reduce((total, [, items]) => total + items.filter(matches).length, 0) +
    unwatchedMatches.length

  return (
    <aside className="flex w-64 shrink-0 flex-col border-r border-border bg-panel-alt">
      <header className="border-b border-border px-3 py-2">
        <div className="mb-1.5 text-2xs font-semibold uppercase tracking-wider text-ink-dim">
          Danh mục theo dõi
        </div>
        <div className="relative">
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Tìm mã hoặc tên (BTC, VCB, vàng…)"
            className="w-full rounded border border-border bg-panel px-2 py-1 pr-6 text-xs text-ink
                       placeholder:text-ink-dim focus:border-accent focus:outline-none"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              title="Xoá tìm kiếm"
              className="absolute right-1 top-1/2 -translate-y-1/2 px-1 text-2xs text-ink-dim
                         hover:text-ink"
            >
              ✕
            </button>
          )}
        </div>
      </header>

      <div className="border-b border-border px-2 py-1.5">
        <AssetTabs value={assetTab} counts={counts} onChange={chooseTab} />
      </div>

      {/* Second tier. Rendered per asset class, never shared: an industry
          chip has no meaning for a currency pair, and a turnover sort has
          none for gold. */}
      {assetTab === 'stock_vn' && industries.length > 0 && (
        <div className="flex flex-wrap gap-1 border-b border-border px-2 py-1.5">
          <FilterChip active={industry === null} onClick={() => setIndustry(null)}>
            Tất cả
          </FilterChip>
          {industries.map(([code, count]) => (
            <FilterChip
              key={code}
              active={industry === code}
              onClick={() => setIndustry(industry === code ? null : code)}
              title={`${INDUSTRY_LABELS[code] ?? code} · ${count} mã`}
            >
              {(INDUSTRY_LABELS[code] ?? code).split(' ')[0]} {count}
            </FilterChip>
          ))}
        </div>
      )}

      {assetTab === 'crypto' && (
        <div className="flex items-center gap-1 border-b border-border px-2 py-1.5">
          <span className="text-2xs text-ink-dim">Sắp theo</span>
          <FilterChip
            active={cryptoSort === 'turnover'}
            onClick={() => setCryptoSort('turnover')}
            title="Giá trị giao dịch 24h — mã nào đang được giao dịch nhiều nhất"
          >
            Khối lượng 24h
          </FilterChip>
          <FilterChip
            active={cryptoSort === 'change'}
            onClick={() => setCryptoSort('change')}
            title="Biên độ thay đổi giá 24h, không phân biệt tăng hay giảm"
          >
            % thay đổi
          </FilterChip>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading && <p className="p-3 text-2xs text-ink-dim">Đang tải…</p>}
        {error && <p className="p-3 text-2xs text-down">Không tải được watchlist</p>}

        {groups.map(([assetClass, items]) => {
          const visible = orderFor(assetClass, items.filter(matches))
          if (visible.length === 0) return null
          // Currencies split into majors and crosses -- the only grouping FX
          // actually has, and with ten pairs it is worth drawing.
          const sections: [string, WatchlistItem[]][] =
            assetClass === 'forex'
              ? ([
                  ['Cặp chính', visible.filter((i) => FX_MAJORS.has(i.symbol.ticker))],
                  ['Cặp chéo', visible.filter((i) => !FX_MAJORS.has(i.symbol.ticker))],
                ].filter(([, rows]) => rows.length > 0) as [string, WatchlistItem[]][])
              : [[ASSET_TAB_LABELS[assetClass] ?? assetClass, visible]]

          return (
            <section key={assetClass}>
              {sections.map(([label, rows]) => (
                <div key={label}>
                  <h3 className="sticky top-0 z-10 bg-panel-hover px-3 py-1 text-2xs font-medium text-ink-dim">
                    {label}
                    <span className="ml-1 opacity-60">{rows.length}</span>
                  </h3>
                  <ul>
                    {rows.map((item) => (
                      <Row
                        key={item.symbol.id}
                        item={item}
                        livePrice={livePrices[item.symbol.id]}
                        active={item.symbol.ticker === selected}
                        needle={needle}
                        signal={openSignals.get(item.symbol.ticker)}
                        onSelect={() => selectTicker(item.symbol.ticker)}
                      />
                    ))}
                  </ul>
                </div>
              ))}
            </section>
          )
        })}

        {unwatchedMatches.length > 0 && (
          <section>
            <h3 className="sticky top-0 z-10 bg-panel-hover px-3 py-1 text-2xs font-medium text-ink-dim">
              Chưa theo dõi
              <span className="ml-1 opacity-60">{unwatchedMatches.length}</span>
            </h3>
            <ul>
              {unwatchedMatches.map((symbol) => (
                <UnwatchedRow
                  key={symbol.id}
                  symbol={symbol}
                  needle={needle}
                  signal={openSignals.get(symbol.ticker)}
                  onSelect={() => selectTicker(symbol.ticker)}
                />
              ))}
            </ul>
          </section>
        )}

        {!isLoading && groups.length === 0 && (
          <p className="p-3 text-2xs text-ink-dim">
            Chưa có mã nào. Chạy <code className="text-ink">ingestion.cli db seed</code>.
          </p>
        )}

        {/* An empty list should say which filter emptied it, otherwise it
            reads as missing data. */}
        {!isLoading && groups.length > 0 && visibleCount === 0 && (
          <p className="p-3 text-2xs text-ink-dim">
            {needle
              ? <>Không có mã nào khớp “{query}”{assetTab !== 'all' && ' trong nhóm này'}.</>
              : <>Nhóm này chưa có mã nào trong danh mục theo dõi.</>}
          </p>
        )}
      </div>
    </aside>
  )
}

function FilterChip({
  active, onClick, title, children,
}: {
  active: boolean
  onClick: () => void
  title?: string
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`rounded px-1.5 py-0.5 text-2xs transition-colors ${
        active ? 'bg-accent/25 text-ink' : 'bg-panel text-ink-dim hover:text-ink'
      }`}
    >
      {children}
    </button>
  )
}

function Row({
  item, livePrice, active, needle, signal, onSelect,
}: {
  item: WatchlistItem
  livePrice: number | undefined
  active: boolean
  needle: string
  signal: TickerSignal | undefined
  onSelect: () => void
}) {
  const mute = useMute()
  const muted = item.muted_until !== null && new Date(item.muted_until) > new Date()

  // The socket price wins when it has arrived; the REST snapshot is the
  // fallback for symbols with no live feed (Yahoo FX/gold is polled).
  const price = livePrice ?? item.last_price
  const change = useMemo(() => {
    if (item.change_pct_24h === null) return null
    if (livePrice === undefined || item.last_price === null || item.last_price === 0) {
      return item.change_pct_24h
    }
    // Carry the REST baseline forward with the live price so the percentage
    // does not freeze while the number above it moves.
    const drift = (livePrice / item.last_price - 1) * 100
    return item.change_pct_24h + drift
  }, [item.change_pct_24h, item.last_price, livePrice])

  return (
    <li>
      <button
        onClick={onSelect}
        className={`group flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors ${
          active ? 'bg-accent/15' : 'hover:bg-panel-hover'
        }`}
      >
        <span className="flex-1 truncate">
          <span className={`text-xs ${active ? 'text-ink' : 'text-ink/90'}`}>
            <Highlight text={item.symbol.ticker} needle={needle} />
          </span>
          <SignalDot signal={signal} />
          {muted && <span className="ml-1 text-2xs text-ink-dim" title="Đang tắt cảnh báo">🔕</span>}
        </span>

        <span className="text-right font-mono text-2xs leading-tight">
          <span className="block text-ink/90">
            {price !== null && price !== undefined
              ? formatPrice(price, item.symbol.price_decimals)
              : '—'}
          </span>
          {change !== null && (
            <span className={change >= 0 ? 'text-up' : 'text-down'}>
              {change >= 0 ? '+' : ''}{change.toFixed(2)}%
            </span>
          )}
        </span>

        <span
          role="button"
          tabIndex={-1}
          title={muted ? 'Bật lại cảnh báo' : 'Tắt cảnh báo 60 phút'}
          onClick={(event) => {
            event.stopPropagation()
            mute.mutate({ ticker: item.symbol.ticker, minutes: muted ? 0 : 60 })
          }}
          className="hidden text-2xs text-ink-dim hover:text-ink group-hover:block"
        >
          {muted ? '🔔' : '🔕'}
        </span>
      </button>
    </li>
  )
}

function UnwatchedRow({
  symbol, needle, signal, onSelect,
}: {
  symbol: Symbol
  needle: string
  signal: TickerSignal | undefined
  onSelect: () => void
}) {
  const add = useAddToWatchlist()
  const { data: watchlists } = useWatchlists()

  const target = watchlists?.find((w) => w.asset_class === symbol.asset_class) ?? watchlists?.[0]

  return (
    <li>
      <button
        onClick={onSelect}
        className="group flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-panel-hover"
      >
        <span className="flex-1 truncate text-xs text-ink/70">
          <Highlight text={symbol.ticker} needle={needle} />
          <SignalDot signal={signal} />
          {symbol.industry_code && (
            <span className="ml-1 text-2xs text-ink-dim opacity-70">
              {INDUSTRY_LABELS[symbol.industry_code] ?? symbol.industry_code}
            </span>
          )}
        </span>
        {target && (
          <span
            role="button"
            tabIndex={-1}
            title={`Thêm vào ${target.name}`}
            onClick={(event) => {
              event.stopPropagation()
              add.mutate({ watchlistId: target.id, ticker: symbol.ticker })
            }}
            className="rounded px-1 text-2xs text-ink-dim hover:bg-accent/25 hover:text-ink"
          >
            +
          </span>
        )}
      </button>
    </li>
  )
}

function groupByAssetClass(watchlists: WatchlistType[]): [AssetClass, WatchlistItem[]][] {
  const groups = new Map<AssetClass, WatchlistItem[]>()
  for (const watchlist of watchlists) {
    for (const item of watchlist.items) {
      const bucket = groups.get(item.symbol.asset_class) ?? []
      if (!bucket.some((existing) => existing.symbol.id === item.symbol.id)) bucket.push(item)
      groups.set(item.symbol.asset_class, bucket)
    }
  }
  const order: AssetClass[] = ['crypto', 'gold', 'forex', 'stock_vn']
  return [...groups.entries()].sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]))
}
